import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ChatWidget } from "@/components/ChatWidget";
import { ScrollText, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

/** Minimal markdown renderer — headings, lists, paragraphs, bold. No deps. */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let ul = null, ol = null;
  const flush = () => {
    if (ul) { out.push({ type: "ul", items: ul }); ul = null; }
    if (ol) { out.push({ type: "ol", items: ol }); ol = null; }
  };
  const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    /^\*\*[^*]+\*\*$/.test(seg) ? <strong key={i}>{seg.slice(2, -2)}</strong> : <span key={i}>{seg}</span>
  );
  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); return; }
    const h = line.match(/^(#{1,6})\s+(.+)$/);
    if (h) { flush(); out.push({ type: `h${h[1].length}`, text: h[2] }); return; }
    if (/^\d+\.\s+/.test(line)) {
      if (ul) { out.push({ type: "ul", items: ul }); ul = null; }
      ol = ol || [];
      ol.push(line.replace(/^\d+\.\s+/, ""));
      return;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (ol) { out.push({ type: "ol", items: ol }); ol = null; }
      ul = ul || [];
      ul.push(line.slice(2));
      return;
    }
    flush();
    out.push({ type: "p", text: line });
  });
  flush();

  return out.map((n, i) => {
    if (n.type === "h1") return <h1 key={i} className="font-display text-3xl mt-6 mb-3">{bold(n.text)}</h1>;
    if (n.type === "h2") return <h2 key={i} className="font-display text-2xl mt-5 mb-2">{bold(n.text)}</h2>;
    if (n.type === "h3") return <h3 key={i} className="font-display text-xl mt-4 mb-2">{bold(n.text)}</h3>;
    if (n.type === "h4") return <h4 key={i} className="font-display text-lg mt-3 mb-1">{bold(n.text)}</h4>;
    if (n.type === "ul") return <ul key={i} className="list-disc ml-6 space-y-1 my-2 text-white/80">{n.items.map((it, j) => <li key={j}>{bold(it)}</li>)}</ul>;
    if (n.type === "ol") return <ol key={i} className="list-decimal ml-6 space-y-1 my-2 text-white/80">{n.items.map((it, j) => <li key={j}>{bold(it)}</li>)}</ol>;
    return <p key={i} className="text-white/80 leading-relaxed my-2">{bold(n.text)}</p>;
  });
}

export default function LegalPage() {
  const { pageType } = useParams();
  const { lang } = useLang();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDoc(null); setNotFound(false);
    shopApi.get(`/shop/legal/${pageType}?lang=${lang}`)
      .then((r) => setDoc(r.data))
      .catch(() => setNotFound(true));
  }, [pageType, lang]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={`legal-page-${pageType}`}>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/shop" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-[color:var(--hq-cyan)] flex items-center justify-center">
              <span className="font-mono text-[10px] text-[color:var(--hq-cyan)]">SH</span>
            </div>
            <div className="font-display font-medium text-sm">EMRKT.CH</div>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/shop" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6" data-testid="legal-back-btn">
          <ArrowLeft size={14} /> {t(lang, "shop_back")}
        </Link>

        {notFound && (
          <div className="text-center py-24">
            <ScrollText className="mx-auto mb-3 text-white/30" size={32} />
            <div className="font-mono text-xs text-white/40">{t(lang, "legal_not_published")}</div>
          </div>
        )}

        {doc && (
          <article data-testid="legal-content">
            <h1 className="font-display text-4xl mb-3">{doc.title}</h1>
            {doc.summary_one_line && (
              <p className="text-white/60 italic mb-6">{doc.summary_one_line}</p>
            )}
            <div className="text-white/40 font-mono text-[10px] uppercase mb-6">
              {t(lang, "legal_last_updated")}: {doc.last_updated_iso}
            </div>
            <div className="text-base">{renderMarkdown(doc.body_md)}</div>
          </article>
        )}
      </div>

      <ChatWidget />
    </div>
  );
}
